// The co-driver in the passenger seat (Adam's request): a rigged figure (tools/blender/
// build_codriver.py) holding a tablet that shows the upcoming pace notes (ui/tablet-notes.ts). In
// the cockpit view the notes live on the tablet instead of the HUD strip.
//
// The body reacts to the car: the torso and head lean against lateral and longitudinal g through
// damped springs (the harness holds the hips, the head moves most), landings and impacts make him
// slump and nod, the tablet bounces with the arms. His head reads the tablet while he calls and
// glances up at the road between calls (and after a hit). All per-frame work reuses preallocated
// objects (no garbage).
import * as THREE from 'three/webgpu';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import type { PaceNote } from '../codriver/notes';
import { TabletNotes } from '../ui/tablet-notes';
import { setEnvScale } from './matte';

export interface CodriverMotion {
  /** Lateral g (+ = acceleration toward the car's left), longitudinal g (+ = forward), vertical g (1 at rest). */
  gLat: number;
  gLong: number;
  gVert: number;
  /** Yaw rate (rad/s, + = turning left). */
  yawRate: number;
  speed: number;
}

/** Where the tablet's notes come from (the pace-note HUD) and whether a call is being read. */
export interface CodriverCue {
  notes(): readonly PaceNote[];
  version(): number;
  speaking(): boolean;
}

/** Damped spring (semi-implicit Euler, sub-stepped for stability). */
class Spring {
  x = 0;
  v = 0;
  constructor(
    private readonly k: number,
    private readonly zeta: number,
  ) {}
  step(target: number, dt: number): number {
    const w = Math.sqrt(this.k);
    const n = Math.max(1, Math.ceil(dt / (1 / 120)));
    const h = dt / n;
    for (let i = 0; i < n; i++) {
      this.v += (this.k * (target - this.x) - 2 * this.zeta * w * this.v) * h;
      this.x += this.v * h;
    }
    return this.x;
  }
}

interface Joint {
  bone: THREE.Object3D;
  rest: THREE.Quaternion;
  /** Parent's rest rotation relative to the figure's root, and its inverse. */
  parent: THREE.Quaternion;
  parentInv: THREE.Quaternion;
}

const _d = new THREE.Quaternion();
const _e = new THREE.Euler(0, 0, 0, 'YXZ');
const _wq = new THREE.Quaternion();

let seed = 9137;
const rnd = (): number => {
  seed = (seed * 16807) % 2147483647;
  return seed / 2147483647;
};

/** Load the co-driver figure (GLB with meshopt compression). */
export async function loadCodriver(url: string): Promise<CodriverAvatar> {
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  return new CodriverAvatar(await loader.loadAsync(url));
}

export class CodriverAvatar {
  readonly root: THREE.Object3D;
  private readonly joints: Record<'spine' | 'chest' | 'neck' | 'head' | 'shoulderL' | 'shoulderR', Joint | null>;
  private readonly tablet = new TabletNotes();
  private readonly texture: THREE.CanvasTexture;
  private cue: CodriverCue | null = null;
  private shownVersion = -1;
  /** Tablet redraws so far (tests). */
  draws = 0;
  // Body springs (radians of lean / normalised heave).
  private readonly torsoLat = new Spring(110, 0.55);
  private readonly torsoLong = new Spring(110, 0.6);
  private readonly headLat = new Spring(55, 0.42);
  private readonly headLong = new Spring(55, 0.45);
  private readonly heave = new Spring(180, 0.32);
  private readonly lookPitch = new Spring(38, 0.85);
  private readonly lookYaw = new Spring(38, 0.85);
  private glance = 0;
  private glanceTimer = 2.5;
  private t = 0;
  private lastGVert = 1;

  constructor(gltf: GLTF) {
    this.root = gltf.scene;
    this.root.name = 'codriver';
    this.root.updateMatrixWorld(true);
    const joint = (name: string): Joint | null => {
      const bone = this.root.getObjectByName(name);
      if (!bone || !bone.parent) return null;
      const parent = new THREE.Quaternion();
      bone.parent.getWorldQuaternion(parent);
      return { bone, rest: bone.quaternion.clone(), parent, parentInv: parent.clone().invert() };
    };
    // glTF node names drop the dots of Blender's bone names (shoulder.L → shoulderL).
    this.joints = {
      spine: joint('spine'),
      chest: joint('chest'),
      neck: joint('neck'),
      head: joint('head'),
      shoulderL: joint('shoulderL') ?? joint('shoulder.L'),
      shoulderR: joint('shoulderR') ?? joint('shoulder.R'),
    };
    this.texture = new THREE.CanvasTexture(this.tablet.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.flipY = false; // glTF UVs run top-down
    this.texture.anisotropy = 4;
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = false;
      m.receiveShadow = true;
      m.frustumCulled = false; // skinned: the bind-pose bounds don't follow the lean
      if (o.name === 'tablet_screen') {
        const mat = new THREE.MeshBasicNodeMaterial({ map: this.texture });
        mat.name = 'tablet_screen';
        m.material = mat;
      } else {
        // Node materials like the car's (car-model.ts), shaded by the roof like the rest of the cabin.
        const src = m.material as THREE.MeshStandardMaterial;
        const s = new THREE.MeshStandardNodeMaterial({ color: src.color ?? new THREE.Color(0x808080), roughness: src.roughness ?? 0.7, metalness: src.metalness ?? 0 });
        s.name = src.name;
        setEnvScale(s, 0.3);
        m.material = s;
      }
    });
  }

  setCue(cue: CodriverCue): void {
    this.cue = cue;
  }

  /** Rotate a bone by `x` (pitch about the car's X), `y` (yaw about Y), `z` (roll about Z), in car axes, about its own pivot. */
  private pose(j: Joint | null, x: number, y: number, z: number): void {
    if (!j) return;
    _e.set(x, y, z, 'YXZ');
    _d.setFromEuler(_e);
    // local' = parent⁻¹ · D · parent · rest
    _wq.copy(j.parentInv).multiply(_d).multiply(j.parent).multiply(j.rest);
    j.bone.quaternion.copy(_wq);
  }

  update(m: CodriverMotion, dt: number, reduceMotion: boolean): void {
    if (dt <= 0) return;
    const d = Math.min(dt, 0.1);
    this.t += d;
    // Tablet: redraw only when the notes change (and once the fonts arrive).
    if (this.cue) {
      const v = this.cue.version();
      if (v !== this.shownVersion || this.tablet.dirty) {
        this.shownVersion = v;
        this.tablet.draw(this.cue.notes());
        this.texture.needsUpdate = true;
        this.draws++;
      }
    }
    const k = reduceMotion ? 0.4 : 1;
    const gLat = Math.max(-2, Math.min(2, m.gLat));
    const gLong = Math.max(-2, Math.min(2, m.gLong));
    // Vertical: a landing or a bottoming spike (and a hit) compresses him; airtime lifts him a little.
    const gv = Math.max(-1, Math.min(4, m.gVert - 1));
    const hv = this.heave.step(gv, d);
    // Torso follows the car with a short lag, the head with a longer, bouncier one (it rolls
    // opposite to the acceleration: a left turn throws him right; braking throws him forward).
    // About 8° of head roll per g with half that in the torso, as harnessed crews show on onboards.
    const tl = this.torsoLat.step(gLat * 0.065 * k, d);
    const tf = this.torsoLong.step(-gLong * 0.04 * k, d);
    const hl = this.headLat.step(gLat * 0.14 * k, d);
    const hf = this.headLong.step(-gLong * 0.1 * k, d);
    // Looking: the head reads the tablet while he calls; between calls he glances up at the road for
    // ~1 s every few seconds; a big hit or landing makes him look up at once.
    const speaking = this.cue?.speaking() ?? false;
    if (Math.abs(m.gVert - this.lastGVert) > 1.6 || m.gVert > 2.6) {
      this.glance = Math.max(this.glance, 1.0);
      this.glanceTimer = 2 + rnd() * 2;
    }
    this.lastGVert = m.gVert;
    this.glanceTimer -= d;
    if (this.glanceTimer <= 0 && !speaking && m.speed > 2) {
      this.glance = 0.7 + rnd() * 0.7;
      this.glanceTimer = 2.5 + rnd() * 3.5;
    }
    this.glance = Math.max(0, this.glance - d);
    const up = this.glance > 0 && !speaking ? 1 : 0;
    // Head up and back toward the road ahead (his rest pose looks down-left at the tablet), turned
    // a little into the corner.
    const lp = this.lookPitch.step(-0.42 * up, d);
    const yawInto = Math.max(-0.25, Math.min(0.25, m.yawRate * 0.12));
    const ly = this.lookYaw.step(-0.28 * up + yawInto * (0.4 + 0.6 * up), d);
    // Breathing.
    const br = 0.006 * Math.sin(this.t * 1.7);
    // Spine and chest share the torso lean; the heave folds him forward a little.
    this.pose(this.joints.spine, tf * 0.5 + hv * 0.025, 0, tl * 0.5);
    this.pose(this.joints.chest, tf * 0.5 + hv * 0.02 + br, 0, tl * 0.5);
    // Neck and head carry the extra head motion and the look direction.
    this.pose(this.joints.neck, hf * 0.45 + hv * 0.04 + lp * 0.4, ly * 0.4, hl * 0.45);
    this.pose(this.joints.head, hf * 0.55 + hv * 0.05 + lp * 0.6, ly * 0.6, hl * 0.55);
    // Arms (and the tablet with them) dip under vertical load and lag the torso.
    const arm = -hv * 0.05 - tf * 0.3;
    this.pose(this.joints.shoulderL, arm, 0, -tl * 0.2);
    this.pose(this.joints.shoulderR, arm, 0, -tl * 0.2);
  }
}
